/**
 * Isometric line drawings for the HubSpot automation process (Discovery,
 * Workflow design, Build and integration, Launch and tuning). Same projection,
 * frame, stroke and animation hooks as the other service drawings:
 * - 01 pipeline stages on a plate; each stage lights up in turn
 * - 02 workflow nodes stack and link trigger → branch → action
 * - 03 HubSpot hub joins two systems; a record travels the channel
 * - 04 reporting bars rise; the newest bar pulses
 */

import type { CSSProperties } from "react";

import {
  box,
  CORNER_CROSSES,
  floorGrid,
  IllustrationFrame,
  motionPath,
  polyline,
  project,
  type Point3,
  type ProcessIllustration,
  type Shape,
} from "@/components/services/seo/seo-process-illustrations";

function dotAt(point: Point3) {
  const [cx, cy] = project(point);
  return { cx, cy };
}

function faceRect(x: number, y: number, z: number, w: number, h: number) {
  return polyline(
    [
      [x, y, z],
      [x + w, y, z],
      [x + w, y, z + h],
      [x, y, z + h],
    ],
    true,
  );
}

// ---------------------------------------------------------------- 01 Discovery

const STAGES: { x: number; w: number; h: number }[] = [
  { x: 14, w: 14, h: 10 },
  { x: 32, w: 14, h: 16 },
  { x: 50, w: 14, h: 22 },
  { x: 68, w: 14, h: 28 },
];

function DiscoveryIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 20, 0, 100, 64, 4),
    ...STAGES.flatMap(({ x, w, h }) => box(x, 72 - h, 4, w, h, 8)),
  ];

  const tops = STAGES.map(({ x, w, h }) => [x + w / 2, 72 - h, 12] as Point3);

  const construct = [
    ...floorGrid(100, 20).filter((_, index) => index % 2 === 0),
    polyline(tops.map((top) => [top[0], top[1], 4] as Point3)),
  ];

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      <g className="cc-ill-hub-discovery__links">
        {tops.slice(1).map((top, index) => (
          <path
            key={index}
            d={polyline([tops[index], top])}
            pathLength={1}
            style={{ "--cc-ill-segment": index } as CSSProperties}
          />
        ))}
      </g>
      {tops.map((top, index) => (
        <circle
          key={index}
          className="cc-ill-hub-discovery__node"
          style={{ "--cc-ill-node": index } as CSSProperties}
          {...dotAt(top)}
          r={3}
        />
      ))}
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 02 Workflow design

const WF_NODES: Point3[] = [
  [50, 22, 14],
  [50, 44, 22],
  [36, 66, 14],
  [64, 66, 14],
];

function WorkflowDesignIllustration({ maskId }: { maskId: string | null }) {
  const nodeBox = (cx: number, cy: number, z: number) => box(cx - 10, cy - 6, z, 20, 12, 6);

  const shapes: Shape[] = [
    ...box(0, 10, 0, 100, 80, 4),
    ...nodeBox(50, 22, 8),
    ...nodeBox(50, 44, 16),
    ...nodeBox(36, 66, 8),
    ...nodeBox(64, 66, 8),
  ];

  const construct = [
    polyline([[10, 90, 0], [90, 90, 0]]),
    polyline([[50, 10, 4], [50, 90, 4]]),
  ];

  const links: [Point3, Point3][] = [
    [WF_NODES[0], WF_NODES[1]],
    [WF_NODES[1], WF_NODES[2]],
    [WF_NODES[1], WF_NODES[3]],
  ];

  return (
    <IllustrationFrame
      layers={[
        { shapes },
        { className: "cc-ill-hub-design__node cc-ill-hub-design__node--1", shapes: nodeBox(50, 22, 8) },
        { className: "cc-ill-hub-design__node cc-ill-hub-design__node--2", shapes: nodeBox(50, 44, 16) },
        { className: "cc-ill-hub-design__node cc-ill-hub-design__node--3", shapes: nodeBox(36, 66, 8) },
        { className: "cc-ill-hub-design__node cc-ill-hub-design__node--4", shapes: nodeBox(64, 66, 8) },
      ]}
      construct={construct}
      crosses={CORNER_CROSSES}
      maskId={maskId}
    >
      <g className="cc-ill-hub-design__links">
        {links.map(([from, to], index) => (
          <path
            key={index}
            d={polyline([from, to])}
            pathLength={1}
            style={{ "--cc-ill-segment": index } as CSSProperties}
          />
        ))}
      </g>
      <path className="cc-ill-hub-design__face" d={faceRect(44, 40, 17, 12, 4)} />
      <circle className="cc-ill__dot" {...dotAt([50, 44, 22])} r={2.6} />
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 03 Build and integration

const SYNC_Z = 20;
const SYNC_PATH: Point3[] = [
  [22, 50, SYNC_Z],
  [50, 50, SYNC_Z],
  [78, 50, SYNC_Z],
];

function BuildIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 26, 0, 100, 48, 4),
    ...box(6, 38, 4, 18, 24, 26),
    ...box(38, 34, 4, 24, 32, 34),
    ...box(76, 38, 4, 18, 24, 22),
    ...box(28, 47, SYNC_Z - 2, 44, 6, 2),
  ];

  const construct = [
    polyline([[6, 38, 30], [94, 38, 30]]),
    polyline([[50, 26, 4], [50, 74, 4]]),
  ];

  const [endX, endY] = project(SYNC_PATH[SYNC_PATH.length - 1]);

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      <path className="cc-ill-hub-build__line" d={polyline(SYNC_PATH)} pathLength={1} />
      <g className="cc-ill-hub-build__packet" transform={`translate(${endX.toFixed(1)} ${endY.toFixed(1)})`}>
        <rect x={-3.5} y={-3.5} width={7} height={7} rx={1.5} />
        <animateMotion
          begin="indefinite"
          dur="1.6s"
          fill="freeze"
          calcMode="spline"
          keyTimes="0;1"
          keySplines="0.45 0 0.25 1"
          path={motionPath(SYNC_PATH)}
        />
      </g>
      <circle className="cc-ill__dot" {...dotAt([50, 50, 38])} r={2.6} />
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 04 Launch and tuning

const BAR_WIDTH = 10;
const BARS = [
  { x: 22, h: 14 },
  { x: 38, h: 22 },
  { x: 54, h: 30 },
  { x: 70, h: 38 },
];

function LaunchIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 24, 0, 100, 56, 4),
    ...box(14, 24, 4, 72, 4, 40),
    ...BARS.flatMap(({ x, h }) => box(x, 64 - h, 4, BAR_WIDTH, h, 6)),
  ];

  const construct = [
    polyline([[14, 64, 44], [86, 64, 44]]),
    ...BARS.map(({ x, h }) => polyline([[x + BAR_WIDTH / 2, 64 - h, 10], [x + BAR_WIDTH / 2, 64 - h, 44]])),
  ];

  const pulseBar = BARS[BARS.length - 1];
  const pulseTop = project([pulseBar.x + BAR_WIDTH / 2, 64 - pulseBar.h, 10]);

  return (
    <IllustrationFrame
      layers={[
        { shapes },
        ...BARS.map(({ x, h }, index) => ({
          className: `cc-ill-hub-launch__bar cc-ill-hub-launch__bar--${index + 1}`,
          shapes: box(x, 64 - h, 4, BAR_WIDTH, h, 6),
        })),
      ]}
      construct={construct}
      crosses={CORNER_CROSSES}
      maskId={maskId}
    >
      <circle className="cc-ill-hub-launch__pulse" cx={pulseTop[0]} cy={pulseTop[1]} r={3} />
      <circle className="cc-ill__dot" cx={pulseTop[0]} cy={pulseTop[1]} r={2.6} />
    </IllustrationFrame>
  );
}

export const HUBSPOT_PROCESS_ILLUSTRATIONS: ProcessIllustration[] = [
  DiscoveryIllustration,
  WorkflowDesignIllustration,
  BuildIllustration,
  LaunchIllustration,
];
